import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  Divider,
  Paper
} from '@mui/material';
import { useData } from '../../context/DataContext';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';

const ManageHospitalInfo = () => {
  const { state, dispatch } = useData();
  const { hospitalInfo } = state;

  const [formData, setFormData] = useState({
    name: hospitalInfo.name,
    address: hospitalInfo.address,
    contact: hospitalInfo.contact,
    mapLink: hospitalInfo.mapLink,
    email: hospitalInfo.email,
    nearbyLocation: hospitalInfo.nearbyLocation,
    showName: hospitalInfo.showName,
    showAddress: hospitalInfo.showAddress,
    showContact: hospitalInfo.showContact,
    showMapLink: hospitalInfo.showMapLink,
    showEmail: hospitalInfo.showEmail,
    showNearbyLocation: hospitalInfo.showNearbyLocation
  });

  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch({
      type: 'UPDATE_HOSPITAL_INFO',
      payload: formData
    });
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  const handleReset = () => {
    setFormData({
      name: hospitalInfo.name,
      address: hospitalInfo.address,
      contact: hospitalInfo.contact,
      mapLink: hospitalInfo.mapLink,
      email: hospitalInfo.email,
      nearbyLocation: hospitalInfo.nearbyLocation,
      showName: hospitalInfo.showName,
      showAddress: hospitalInfo.showAddress,
      showContact: hospitalInfo.showContact,
      showMapLink: hospitalInfo.showMapLink,
      showEmail: hospitalInfo.showEmail,
      showNearbyLocation: hospitalInfo.showNearbyLocation
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BusinessIcon sx={{ fontSize: 32, color: '#1976d2', mr: 2 }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Manage Hospital Information
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Update hospital details and control which information is displayed on the website.
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Hospital information updated successfully!
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          {/* Hospital Name */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Hospital Name
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.showName}
                        onChange={handleChange}
                        name="showName"
                        color="primary"
                      />
                    }
                    label="Show"
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Hospital Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!formData.showName}
                  sx={{ mb: 2 }}
                />
                <Typography variant="caption" color="text.secondary">
                  This name will be displayed throughout the website
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Number */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Contact Number
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.showContact}
                        onChange={handleChange}
                        name="showContact"
                        color="primary"
                      />
                    }
                    label="Show"
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Contact Number"
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  disabled={!formData.showContact}
                  sx={{ mb: 2 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Main hospital contact number
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Email */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Email Address
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.showEmail}
                        onChange={handleChange}
                        name="showEmail"
                        color="primary"
                      />
                    }
                    label="Show"
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!formData.showEmail}
                  sx={{ mb: 2 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Main hospital email address
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Map Link */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Map Link
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.showMapLink}
                        onChange={handleChange}
                        name="showMapLink"
                        color="primary"
                      />
                    }
                    label="Show"
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Google Maps Link"
                  name="mapLink"
                  value={formData.mapLink}
                  onChange={handleChange}
                  disabled={!formData.showMapLink}
                  sx={{ mb: 2 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Google Maps URL for hospital location
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Address */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Hospital Address
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.showAddress}
                        onChange={handleChange}
                        name="showAddress"
                        color="primary"
                      />
                    }
                    label="Show"
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Complete Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={!formData.showAddress}
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Full hospital address for display on website
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Nearby Location */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Nearby Location
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.showNearbyLocation}
                        onChange={handleChange}
                        name="showNearbyLocation"
                        color="primary"
                      />
                    }
                    label="Show"
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Nearby Landmarks/Location"
                  name="nearbyLocation"
                  value={formData.nearbyLocation}
                  onChange={handleChange}
                  disabled={!formData.showNearbyLocation}
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Nearby landmarks or location details to help visitors find the hospital
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleReset}
            sx={{ px: 4, py: 1.5 }}
          >
            Reset
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ px: 4, py: 1.5 }}
          >
            Save Changes
          </Button>
        </Box>
      </form>

      {/* Preview Section */}
      <Paper sx={{ mt: 6, p: 4, bgcolor: '#f8f9fa' }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Preview - How it will appear on the website:
        </Typography>
        <Grid container spacing={3}>
          {formData.showName && (
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" color="text.secondary">Hospital Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.name}</Typography>
              </Box>
            </Grid>
          )}
          {formData.showContact && (
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" color="text.secondary">Contact</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.contact}</Typography>
              </Box>
            </Grid>
          )}
          {formData.showEmail && (
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.email}</Typography>
              </Box>
            </Grid>
          )}
          {formData.showAddress && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.address}</Typography>
              </Box>
            </Grid>
          )}
          {formData.showNearbyLocation && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" color="text.secondary">Nearby Location</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{formData.nearbyLocation}</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Container>
  );
};

export default ManageHospitalInfo; 